import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditOutlined from "@mui/icons-material/EditOutlined";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 6,
    padding: "8px 10px",
    backgroundColor: "rgba(0,0,0,0.035)",
    minHeight: 72,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    boxSizing: "border-box",
    width: "100%",
    cursor: "default",
  },
  rootClickable: {
    cursor: "pointer",
  },
  titleRow: {
    width: "100%",
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 4,
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
  },
  toggle: {
    fontSize: 11,
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  line1: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  line2: {
    marginTop: 2,
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  muted: {
    opacity: 0.8,
  },
  noPrint: {
    "@media print": { display: "none" },
  },
  editBtn: {
    padding: 4,
    marginLeft: "auto",
    opacity: 0.25,
    "&:hover": { opacity: 0.6 },
  },
  dirty: {
    backgroundColor: "rgba(255,193,7,0.15)",
    boxShadow: "inset 4px 0 0 #f7bd02",
  },
}));

const join = (arr) => arr.filter(Boolean).join(" | ");

const AddressCompact = ({
  title,
  addressFields,
  dirty = false,
  showToggle,
  expanded,
  onToggle,
  onEdit,
  enableClickToEdit,
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const fields = useMemo(
    () =>
      Object.fromEntries(
        (addressFields || []).map(({ field, value }) => [field, value])
      ),
    [addressFields]
  );

  const mainLine = [
    fields.Address1,
    fields.Address2,
    fields.Address3,
    fields.Address4,
    fields.Address5,
  ]
    .filter(Boolean)
    .join(", ");

  const locationLine = [
    fields.City,
    fields.Zip,
    fields.Country,
  ]
    .filter(Boolean)
    .join(" ");

  const metaLine = join([
    fields.Reference ? `Ref: ${ fields.Reference } ` : null,
    fields.Contact ? `Kontakt: ${ fields.Contact } ` : null,
    fields.Telephone ? `Tel: ${ fields.Telephone } ` : null,
    fields.Email ? `E - post: ${ fields.Email } ` : null,
  ]);

  return (
    <Box
      className={[
        classes.root,
        classes.noPrint,
        dirty ? classes.dirty : null,
        enableClickToEdit && onEdit ? classes.rootClickable : null,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={enableClickToEdit && onEdit ? onEdit : undefined}
    >
      <Box className={classes.titleRow}>
        <Typography className={classes.title}>{title}</Typography>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {showToggle && typeof onToggle === "function" && (
            <Link
              component="button"
              type="button"
              className={classes.toggle}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              underline="none"
            >
              {expanded
                ? intl.formatMessage({
                    id: "incomingOrders.addresses.hideFullAddress",
                  })
                : intl.formatMessage({
                    id: "incomingOrders.addresses.showFullAddress",
                  })}
            </Link>
          )}

          {onEdit && (
            <Tooltip
              title={intl.formatMessage({
                id: "incomingOrders.addresses.edit",
              })}
            >
              <IconButton
                size="small"
                className={classes.editBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <EditOutlined fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography className={classes.line1} title={mainLine || locationLine}>
        {mainLine || "—"}
        {locationLine ? ` • ${ locationLine } ` : ""}
      </Typography>

      {metaLine ? (
        <Typography
          className={`${ classes.line2 } ${ classes.muted } `}
          title={metaLine}
        >
          {metaLine}
        </Typography>
      ) : null}
    </Box>
  );
};

AddressCompact.propTypes = {
  title: PropTypes.string.isRequired,
  addressFields: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  dirty: PropTypes.bool,
  showToggle: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onEdit: PropTypes.func,
  enableClickToEdit: PropTypes.bool,
};

AddressCompact.defaultProps = {
  addressFields: [],
  showToggle: false,
  expanded: false,
  onToggle: null,
  onEdit: null,
  enableClickToEdit: false,
};

export default AddressCompact;